import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Navbar,
  Button,
  Input,
  LoadingSpinner,
  NoEventsState,
  ErrorState,
} from '../components';
import { eventService, friendService } from '../services';
import { useUser, useAuth } from '../context';

/**
 * Host Dashboard Page - Find Your People
 * Events table with people search and suggested friends
 */
export default function HostDashboardPage() {
  const navigate = useNavigate();
  const { profile, walletBalance } = useUser();
  const { logout } = useAuth();

  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // People search state
  const [peopleSearch, setPeopleSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Suggested friends state
  const [suggestedFriends, setSuggestedFriends] = useState([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(true);

  // Fetch events
  const fetchEvents = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await eventService.getEvents();

      if (result.success) {
        setEvents(result.data || []);
      } else {
        setError(result.error || 'Failed to load events');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Fetch suggested friends
  useEffect(() => {
    const fetchSuggestions = async () => {
      setIsLoadingSuggestions(true);
      const result = await friendService.getSuggestedFriends(6);
      if (result.success) {
        setSuggestedFriends(result.data || []);
      }
      setIsLoadingSuggestions(false);
    };

    fetchSuggestions();
  }, []);

  // Search for people (debounced)
  useEffect(() => {
    if (!peopleSearch.trim() || peopleSearch.length < 2) {
      setSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsSearching(true);
      const result = await friendService.searchPeople(peopleSearch);
      if (result.success) {
        setSearchResults(result.data || []);
      }
      setIsSearching(false);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [peopleSearch]);

  const handleSendFriendRequest = async (userId) => {
    const result = await friendService.sendFriendRequest(userId);
    if (result.success) {
      // Update search results
      setSearchResults((prev) =>
        prev.map((p) => (p.userId === userId ? { ...p, requestSent: true } : p))
      );
      // Update suggested friends
      setSuggestedFriends((prev) =>
        prev.map((p) => (p.userId === userId ? { ...p, requestSent: true } : p))
      );
    }
  };

  const handleCreateEvent = () => {
    navigate('/host/events/create');
  };

  const handleEventClick = (eventId) => {
    navigate(`/host/events/${eventId}`);
  };

  const handleModeChange = (mode) => {
    if (mode === 'guest') {
      navigate('/guest');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getStatusDisplay = (status) => {
    const statusMap = {
      draft: { label: 'DRAFT', color: 'bg-gray-200 text-gray-700' },
      published: { label: 'LIVE', color: 'bg-green-500 text-white' },
      pledging: { label: 'LIVE', color: 'bg-green-500 text-white' },
      greenlighted: { label: 'LIVE', color: 'bg-green-500 text-white' },
      completed: { label: 'ENDED', color: 'bg-black text-white' },
      cancelled: { label: 'CANCELLED', color: 'bg-red-500 text-white' },
    };
    return statusMap[status] || statusMap.draft;
  };

  const filterTabs = [
    { key: 'all', label: 'ALL' },
    { key: 'draft', label: 'DRAFT' },
    { key: 'published', label: 'LIVE' },
    { key: 'completed', label: 'ENDED' },
  ];

  // Filter events for display
  const filteredEvents = events.filter((event) => {
    if (filter === 'all') return true;
    if (filter === 'published') return event.status === 'published' || event.status === 'pledging' || event.status === 'greenlighted';
    if (filter === 'completed') return event.status === 'completed' || event.status === 'cancelled';
    return event.status === filter;
  });

  // Calculate party fund and shot clock for each event
  const getPartyFund = (event) => {
    const totalPledged = event.totalPledgedCents || 0;
    const fundingGoal = event.fundingGoalCents || 0;
    const percentage = fundingGoal > 0 ? Math.round((totalPledged / fundingGoal) * 100) : 0;
    return { amount: totalPledged, percentage };
  };

  const getShotClock = (event) => {
    if (!event.dateTime || event.status === 'draft') return '-';
    const eventDate = new Date(event.dateTime);
    const now = new Date();
    const diffTime = eventDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return 'PAST';
    if (diffDays === 0) return 'TODAY';
    if (diffDays === 1) return '1 DAY';
    return `${diffDays} DAYS`;
  };

  return (
    <div className="min-h-screen bg-gray-50/50">
      <Navbar
        mode="host"
        onModeChange={handleModeChange}
        user={profile}
        walletBalance={walletBalance}
        onLogout={handleLogout}
        onLogoClick={() => navigate('/')}
      />

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-mono text-3xl md:text-4xl font-bold uppercase tracking-wider text-black mb-1">
              FIND YOUR PEOPLE
            </h1>
            <p className="font-mono text-lg text-gray-600">
              Gather them
            </p>
          </div>
          <Button variant="primary" onClick={handleCreateEvent}>
            + CREATE PARTY
          </Button>
        </div>

        {/* Events table */}
        <div className="border-2 border-black bg-white mb-8">
          {/* Filter tabs */}
          <div className="border-b-2 border-black">
            <div className="flex overflow-x-auto">
              {filterTabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setFilter(tab.key)}
                  className={`
                    px-6 py-4
                    font-mono text-sm font-semibold uppercase tracking-wider
                    border-r-2 border-black last:border-r-0
                    transition-colors duration-200
                    whitespace-nowrap
                    ${filter === tab.key
                      ? 'bg-black text-white'
                      : 'bg-white text-black hover:bg-gray-100'
                    }
                  `}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {isLoading ? (
            <div className="p-12">
              <LoadingSpinner label="Loading events..." />
            </div>
          ) : error ? (
            <ErrorState message={error} onRetry={fetchEvents} />
          ) : filteredEvents.length === 0 ? (
            <NoEventsState onCreateEvent={handleCreateEvent} />
          ) : (
            <>
              {/* Table header */}
              <div className="hidden md:grid md:grid-cols-12 gap-4 px-6 py-3 border-b-2 border-black bg-gray-50">
                <div className="col-span-3">
                  <span className="font-mono text-xs font-bold uppercase tracking-wider text-gray-600">
                    NAME
                  </span>
                </div>
                <div className="col-span-2">
                  <span className="font-mono text-xs font-bold uppercase tracking-wider text-gray-600">
                    STATUS
                  </span>
                </div>
                <div className="col-span-2 text-center">
                  <span className="font-mono text-xs font-bold uppercase tracking-wider text-gray-600">
                    PLEDGES
                  </span>
                </div>
                <div className="col-span-3 text-center">
                  <span className="font-mono text-xs font-bold uppercase tracking-wider text-gray-600">
                    PARTY FUND
                  </span>
                </div>
                <div className="col-span-2 text-right">
                  <span className="font-mono text-xs font-bold uppercase tracking-wider text-gray-600">
                    SHOT. CLOCK
                  </span>
                </div>
              </div>

              {/* Table rows */}
              <div className="divide-y-2 divide-black">
                {filteredEvents.map((event) => {
                  const statusDisplay = getStatusDisplay(event.status);
                  const partyFund = getPartyFund(event);
                  const shotClock = getShotClock(event);

                  return (
                    <div
                      key={event.id}
                      onClick={() => handleEventClick(event.id)}
                      className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      {/* Mobile layout */}
                      <div className="md:hidden">
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <h3 className="font-mono text-sm font-bold uppercase tracking-wider text-black">
                            {event.title || event.name}
                          </h3>
                          <span
                            className={`
                              px-2 py-1
                              font-mono text-xs font-semibold uppercase tracking-wider
                              ${statusDisplay.color}
                            `}
                          >
                            {statusDisplay.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 flex-wrap">
                          <span className="font-mono text-xs text-gray-500 uppercase tracking-wider">
                            {event.applicationCount || 0}/{event.capacity || '---'} PLEDGES
                          </span>
                          <span className="font-mono text-xs text-gray-500 uppercase tracking-wider">
                            P${(partyFund.amount / 100).toFixed(0)} ({partyFund.percentage}%)
                          </span>
                          <span className="font-mono text-xs text-gray-500 uppercase tracking-wider">
                            {shotClock}
                          </span>
                        </div>
                      </div>

                      {/* Desktop layout */}
                      <div className="hidden md:grid md:grid-cols-12 gap-4 items-center">
                        <div className="col-span-3">
                          <h3 className="font-mono text-sm font-bold uppercase tracking-wider text-black truncate">
                            {event.title || event.name}
                          </h3>
                        </div>
                        <div className="col-span-2">
                          <span
                            className={`
                              inline-block px-3 py-1
                              font-mono text-xs font-semibold uppercase tracking-wider
                              ${statusDisplay.color}
                            `}
                          >
                            {statusDisplay.label}
                          </span>
                        </div>
                        <div className="col-span-2 text-center">
                          <span className="font-mono text-lg font-bold text-black">
                            {event.applicationCount || 0}
                          </span>
                          <span className="font-mono text-xs text-gray-500 ml-1">
                            / {event.capacity || '---'}
                          </span>
                        </div>
                        <div className="col-span-3 text-center">
                          <span className="font-mono text-sm font-bold text-black">
                            P${(partyFund.amount / 100).toFixed(0)}
                          </span>
                          <span className="font-mono text-xs text-gray-500 ml-1">
                            ({partyFund.percentage}%)
                          </span>
                        </div>
                        <div className="col-span-2 text-right">
                          <span className="font-mono text-sm text-gray-700 uppercase tracking-wider">
                            {shotClock}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* People You May Know Section */}
        <div className="border-2 border-black bg-white mb-8">
          <div className="px-6 py-4 border-b-2 border-black">
            <h2 className="font-mono text-sm font-bold uppercase tracking-wider text-black">
              PEOPLE YOU MAY KNOW
            </h2>
          </div>

          {isLoadingSuggestions ? (
            <div className="p-8">
              <LoadingSpinner label="Finding people..." />
            </div>
          ) : suggestedFriends.length === 0 ? (
            <div className="p-8 text-center">
              <p className="font-mono text-sm text-gray-500 uppercase tracking-wider">
                No suggestions available
              </p>
            </div>
          ) : (
            <div className="p-6">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
                {suggestedFriends.map((person) => (
                  <div
                    key={person.userId}
                    className="border-2 border-black p-4 flex flex-col items-center bg-white"
                  >
                    <button
                      type="button"
                      onClick={() => navigate(`/users/${person.userId}`)}
                      className="flex flex-col items-center w-full hover:opacity-70 transition-opacity"
                    >
                      <div className="w-16 h-16 mb-3 border-2 border-black overflow-hidden bg-gray-100">
                        {person.avatarUrl ? (
                          <img
                            src={person.avatarUrl}
                            alt={person.displayName}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="font-mono text-xl text-gray-400">?</span>
                          </div>
                        )}
                      </div>
                      <p className="font-mono text-xs font-bold uppercase tracking-wider text-black text-center truncate w-full mb-3">
                        {person.displayName}
                      </p>
                    </button>
                    <Button
                      size="sm"
                      variant={person.requestSent ? 'secondary' : 'primary'}
                      fullWidth
                      onClick={() => handleSendFriendRequest(person.userId)}
                      disabled={person.requestSent}
                    >
                      {person.requestSent ? 'SENT' : '+ ADD'}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* People Search Section */}
        <div className="border-2 border-black bg-white p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search by Name or PR Handle..."
                value={peopleSearch}
                onChange={(e) => setPeopleSearch(e.target.value)}
              />
            </div>
            <Button
              variant="primary"
              disabled={!peopleSearch.trim() || isSearching}
            >
              {isSearching ? 'SEARCHING...' : 'SEARCH'}
            </Button>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="mt-4 divide-y divide-gray-200">
              {searchResults.map((person) => (
                <div key={person.userId} className="py-3 flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => navigate(`/users/${person.userId}`)}
                    className="flex items-center gap-4 flex-1 min-w-0 text-left hover:opacity-70 transition-opacity"
                  >
                    <div className="w-10 h-10 border-2 border-black overflow-hidden flex-shrink-0 bg-gray-100">
                      {person.avatarUrl ? (
                        <img src={person.avatarUrl} alt={person.displayName} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="font-mono text-sm text-gray-400">?</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-sm font-bold uppercase tracking-wider text-black truncate">
                        {person.displayName}
                      </p>
                      {person.handle && (
                        <p className="font-mono text-xs text-gray-500">@{person.handle}</p>
                      )}
                    </div>
                  </button>
                  <Button
                    size="sm"
                    variant={person.requestSent ? 'secondary' : 'primary'}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSendFriendRequest(person.userId);
                    }}
                    disabled={person.requestSent}
                  >
                    {person.requestSent ? 'SENT' : '+ ADD'}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
